"use client"

import React, { useEffect } from "react"
import { useRouter } from "next/navigation"
import { getUserRedirectUrl } from "@/lib/userUtils"
import AuthLayoutBase from "@/components/AuthLayoutBase"
import { useSession } from "@/lib/auth/client"
import { AuthLayoutProps } from "@/components/AuthLayoutProps"
import { TurkwiseLoader } from "@turkwise/design-system"

// Extracted loading UI to reduce complexity in the main part
const LoadingPanel: React.FC = () => (
  <AuthLayoutBase titleKey="common.loading">
    <div className="twds-auth-form__status">
      <div className="twds-auth-form__status-content">
        <TurkwiseLoader />
        <span>Giriş yapılıyor...</span>
      </div>
    </div>
  </AuthLayoutBase>
)

// Hook to encapsulate redirect logic (reduces complexity in component body)
// Yönlendirme yalnızca Platform session status === 'authenticated' olduğunda yapılır.
const useAuthRedirect = (
  user: Record<string, unknown> | undefined,
  status: string,
  router: ReturnType<typeof useRouter>,
) => {
  useEffect(() => {
    // Eğer oturum henüz doğrulanmadıysa veya kullanıcı yoksa çık
    if (status !== "authenticated" || !user) return

    console.info("AuthLayout: User authenticated, redirecting...", {
      user,
      status,
    })
    const redirectUrl = getUserRedirectUrl(user)
    console.info("AuthLayout: Redirect URL:", redirectUrl)
    try {
      // replace ile yönlendirme: geri butonunda bu sayfaya dönülmesini engeller
      router.replace(redirectUrl)
    } catch (err) {
      console.warn("AuthLayout: router.replace failed for redirect", redirectUrl, err)
    }
  }, [user, status, router])
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  titleKey,
  title,
  subtitleKey,
  subtitle,
  footerTextKey,
  footerLink,
}) => {
  const router = useRouter()
  const { data: session, status } = useSession()
  const user = session?.user
  const isLoading = status === "loading"

  // handle redirect side effect in a small hook
  useAuthRedirect(user, status, router)

  if (isLoading) return <LoadingPanel />

  if (user) return null

  return (
    <AuthLayoutBase
      titleKey={titleKey}
      title={title}
      subtitleKey={subtitleKey}
      subtitle={subtitle}
      footerTextKey={footerTextKey}
      footerLink={footerLink}
    >
      {children}
    </AuthLayoutBase>
  )
}
